// Pure, side-effect-free tax-notes engine helpers.
// Extracted from JurisdictionManager.jsx to enable unit-level regression testing.
// Contract: every function here is a pure function of its inputs — no React, no network, no DOM.
//
// Covered helpers:
//   - generateSplitInstallmentNotes : unified narrative for split-jurisdiction SEMI-ANNUAL cycles
//   - generateSplitQuarterlyNotes   : unified narrative for split-jurisdiction QUARTERLY cycles
//   - classifyAdjustment            : date-aware fee-vs-penalty-vs-discount classifier
//   - statusWithQualifier           : status text with fee/penalty/discount qualifier appended
//   - buildByYear                   : helper used to mirror the routing in computeCombinedNotes()
//
// See Jest tests in ./__tests__/taxNotesEngine.test.js

// Inline copy of toTitleCase — keeps this module dependency-free
// (utils/formatters.js transitively imports date-fns-tz which is ESM-only
// and breaks CRA's Jest default transform). Must stay in sync semantically.
const toTitleCase = (s) => {
  if (!s) return '';
  return String(s).replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substring(1).toLowerCase());
};

// ---------------------------------------------------------------------------
// Shared small helpers
// ---------------------------------------------------------------------------

const formatCurrency = (val) => {
  const num = parseFloat(val) || 0;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (dateVal) => {
  if (!dateVal) return null;
  if (typeof dateVal === 'string' && dateVal.includes('/')) return dateVal;
  if (typeof dateVal === 'string' && dateVal.includes('-')) {
    const parts = dateVal.split('-');
    if (parts.length === 3) return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }
  return dateVal;
};

// ---------------------------------------------------------------------------
// Fee vs Penalty classification (date-aware)
// ---------------------------------------------------------------------------
/**
 * Classify a positive/negative discount_penalty adjustment:
 *   - discount_penalty  <  0  → 'discount'
 *   - discount_penalty  >  0  → 'fee'     (paid on/before due_date)
 *   - discount_penalty  >  0  → 'penalty' (paid AFTER due_date, or missing paid_date)
 *   - discount_penalty  == 0  → null
 *
 * This mirrors JurisdictionManager.jsx getDpQualifier() (line ~1843) and must stay
 * in lock-step with the summary-status rendering in pdf_service.py & TaxCertificatePanel.jsx.
 *
 * @param {object}  args
 * @param {number}  args.discountPenalty    signed amount (positive = surcharge, negative = discount)
 * @param {string|Date|null} args.paidDate  payment date (ISO 'YYYY-MM-DD' or Date); optional
 * @param {string|Date|null} args.dueDate   due date  (ISO 'YYYY-MM-DD' or Date); optional
 * @returns {'discount' | 'fee' | 'penalty' | null}
 */
export const classifyAdjustment = ({ discountPenalty, paidDate, dueDate }) => {
  const dp = parseFloat(discountPenalty) || 0;
  if (dp < 0) return 'discount';
  if (dp > 0) {
    const pd = paidDate ? new Date(paidDate) : null;
    const dd = dueDate ? new Date(dueDate) : null;
    if (pd && dd && pd <= dd) return 'fee';
    return 'penalty';
  }
  return null;
};

/**
 * Human-readable status text with qualifier appended.
 * Mirrors JurisdictionManager.jsx statusWithQualifier() (~line 1857).
 * @param {string} status     raw status ('paid' | 'due' | 'delinquent' | …)
 * @param {'discount'|'fee'|'penalty'|null|undefined} qualifier
 * @returns {string}
 */
export const statusWithQualifier = (status, qualifier) => {
  if (!qualifier) return status;
  if (qualifier === 'discount') {
    if (status === 'paid') return 'paid at a discount';
    if (status === 'due') return 'due at a discount';
  } else if (qualifier === 'penalty') {
    if (status === 'paid') return 'paid with penalty';
    if (status === 'due') return 'due with penalty';
  } else if (qualifier === 'fee') {
    if (status === 'paid') return 'paid with additional fees';
    if (status === 'due') return 'due with additional fees';
  }
  return status;
};

// ---------------------------------------------------------------------------
// Routing helper — mirrors the detection path in computeCombinedNotes()
// ---------------------------------------------------------------------------
/**
 * Flatten jurisdictions → byYear map, tagging each installment with _sourceJurName.
 * Callers then pass the byYear into generateSplitInstallmentNotes / generateSplitQuarterlyNotes.
 * @param {Array<{jurisdiction_name?: string, installments?: Array}>} jurs
 */
export const buildByYear = (jurs) => {
  const allInstallments = [];
  for (const jur of jurs || []) {
    for (const inst of jur.installments || []) {
      allInstallments.push({ ...inst, _sourceJurName: jur.jurisdiction_name || 'Unknown' });
    }
  }
  const byYear = {};
  for (const inst of allInstallments) {
    const yr = inst.tax_year || 'Unknown';
    if (!byYear[yr]) byYear[yr] = [];
    byYear[yr].push(inst);
  }
  return byYear;
};

/**
 * Detect whether any tax year has installments sourced from 2+ distinct jurisdictions.
 * @param {object} byYear result of buildByYear()
 */
export const hasSplitPattern = (byYear) =>
  Object.values(byYear).some((yearInsts) => {
    if (!yearInsts || yearInsts.length < 2) return false;
    const jurNames = new Set(yearInsts.map((i) => i._sourceJurName));
    return jurNames.size > 1;
  });

// ---------------------------------------------------------------------------
// Generator 1: Semi-annual split-jurisdiction narrative
// ---------------------------------------------------------------------------
/**
 * Generate unified notes for split-installment semi-annual cases.
 * When 1st installment is collected by one jurisdiction and 2nd by another,
 * merge into a single combined narrative instead of per-jurisdiction notes.
 */
// eslint-disable-next-line no-unused-vars
export const generateSplitInstallmentNotes = (jurs, byYear, stateCode) => {
  const notes = [];

  const sortedYears = Object.keys(byYear).sort();
  const currentYear = sortedYears.length > 0 ? sortedYears[sortedYears.length - 1] : '';
  const priorYearInsts = sortedYears
    .filter((yr) => yr < currentYear)
    .flatMap((yr) => byYear[yr]);
  const allPriorPaid =
    priorYearInsts.length === 0 ||
    priorYearInsts.every((inst) => {
      const status = (inst.pay_status || '').toLowerCase();
      return status === 'paid' || status === 'exempt';
    });
  const hasDelinquentPrior = priorYearInsts.some((inst) => {
    const status = (inst.pay_status || '').toLowerCase();
    return status === 'delinquent' || status.includes('partially');
  });

  if (allPriorPaid && !hasDelinquentPrior) {
    notes.push('All prior taxes are paid.');
  }

  for (const yr of sortedYears) {
    const yearInsts = byYear[yr];

    const first = yearInsts.find((i) => {
      const n = (i.installment_name || '').toLowerCase();
      return n.includes('1st') || n.includes('first');
    });
    const second = yearInsts.find((i) => {
      const n = (i.installment_name || '').toLowerCase();
      return n.includes('2nd') || n.includes('second');
    });

    if (!first && !second) continue;

    const isSplit = first && second && first._sourceJurName !== second._sourceJurName;

    if (!isSplit) {
      for (const inst of yearInsts) {
        const status = (inst.pay_status || '').toLowerCase();
        if (status === 'paid') {
          notes.push(`${yr} taxes have been paid in full.`);
        } else if (status === 'due') {
          notes.push(`${yr} taxes are due.`);
        } else if (status === 'delinquent' || status.includes('partially')) {
          notes.push(`${yr} taxes are ${status}.`);
        }
      }
      continue;
    }

    const getStatusText = (inst) => {
      if (!inst) return null;
      const status = (inst.pay_status || '').toLowerCase();
      const jurName = toTitleCase(inst._sourceJurName || '');
      const paidAmt = parseFloat(inst.paid_amount) || 0;
      const balance = parseFloat(inst.balance_amount) || 0;

      if (status === 'paid') return `paid in full to ${jurName}`;
      if (status === 'due') return `due to ${jurName}`;
      if (status === 'delinquent') return `delinquent to ${jurName}`;
      if (status.includes('partially')) {
        if (paidAmt > 0 && balance > 0) {
          return `partially delinquent to ${jurName} (${formatCurrency(paidAmt)} paid, ${formatCurrency(balance)} remaining)`;
        }
        return `partially delinquent to ${jurName}`;
      }
      return null;
    };

    const firstStatus = getStatusText(first);
    const secondStatus = getStatusText(second);

    const parts = [];
    if (firstStatus) parts.push(`${yr} first installment taxes have been ${firstStatus}`);
    if (secondStatus) parts.push(`The second installment taxes are ${secondStatus}`);

    if (parts.length > 0) notes.push(parts.join('. ') + '.');
  }

  notes.push('Taxes are collected semi-annually.');

  for (const yr of sortedYears) {
    const yearInsts = byYear[yr];
    const first = yearInsts.find((i) => {
      const n = (i.installment_name || '').toLowerCase();
      return n.includes('1st') || n.includes('first');
    });
    const second = yearInsts.find((i) => {
      const n = (i.installment_name || '').toLowerCase();
      return n.includes('2nd') || n.includes('second');
    });

    if (first) {
      const dueStr = fmtDate(first.due_date);
      const dlqStr = fmtDate(first.dlq_date);
      if (dueStr) {
        const firstStatus = (first.pay_status || '').toLowerCase();
        const verb = firstStatus === 'paid' ? 'was due' : 'is due';
        let line = `The first installment ${verb} ${dueStr}`;
        if (dlqStr) line += ` and becomes delinquent after ${dlqStr}`;
        notes.push(line + '.');
      }
    }

    if (second) {
      const dueStr = fmtDate(second.due_date);
      const dlqStr = fmtDate(second.dlq_date);
      if (dueStr) {
        const secondStatus = (second.pay_status || '').toLowerCase();
        const verb = secondStatus === 'paid' ? 'was due' : 'is due';
        let line = `The second installment ${verb} ${dueStr}`;
        if (dlqStr) line += ` and becomes delinquent after ${dlqStr}`;
        notes.push(line + '.');
      }
    }
  }

  return notes.join(' ');
};

// ---------------------------------------------------------------------------
// Generator 2: Quarterly split-jurisdiction narrative
// ---------------------------------------------------------------------------
/**
 * Generate unified notes for split-jurisdiction quarterly tax cycles.
 * When quarters are split across jurisdictions (e.g., Q1/Q2 → County, Q3/Q4 → City),
 * merge into a single combined narrative.
 */
// eslint-disable-next-line no-unused-vars
export const generateSplitQuarterlyNotes = (jurs, byYear, stateCode) => {
  const notes = [];
  const QUARTER_ORDINALS = ['first', 'second', 'third', 'fourth'];

  const getQuarterIndex = (inst) => {
    const name = (inst.installment_name || '').toLowerCase();
    if (name.includes('1st') || name.includes('first') || name.endsWith('-1') || name.includes('q1')) return 0;
    if (name.includes('2nd') || name.includes('second') || name.endsWith('-2') || name.includes('q2')) return 1;
    if (name.includes('3rd') || name.includes('third') || name.endsWith('-3') || name.includes('q3')) return 2;
    if (name.includes('4th') || name.includes('fourth') || name.endsWith('-4') || name.includes('q4')) return 3;
    const match = name.match(/(\d+)$/);
    if (match) {
      const num = parseInt(match[1], 10);
      if (num >= 1 && num <= 4) return num - 1;
    }
    return -1;
  };

  const sortedYears = Object.keys(byYear).sort();
  const currentYear = sortedYears.length > 0 ? sortedYears[sortedYears.length - 1] : '';
  const priorYearInsts = sortedYears
    .filter((yr) => yr < currentYear)
    .flatMap((yr) => byYear[yr]);
  const allPriorPaid =
    priorYearInsts.length === 0 ||
    priorYearInsts.every((inst) => {
      const status = (inst.pay_status || '').toLowerCase();
      return status === 'paid' || status === 'exempt';
    });
  if (allPriorPaid) notes.push('All prior taxes are paid.');

  for (const yr of sortedYears) {
    const yearInsts = byYear[yr];
    const sorted = [...yearInsts].sort((a, b) => getQuarterIndex(a) - getQuarterIndex(b));

    const groups = [];
    for (const inst of sorted) {
      const qi = getQuarterIndex(inst);
      if (qi < 0) continue;
      const jurName = inst._sourceJurName || 'Unknown';
      const status = (inst.pay_status || 'due').toLowerCase();
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.jurName === jurName && lastGroup.status === status) {
        lastGroup.quarters.push(qi);
      } else {
        groups.push({ jurName, status, quarters: [qi] });
      }
    }

    if (groups.length === 0) continue;

    const phraseParts = [];
    for (const group of groups) {
      const qLabels = group.quarters.map((qi) => QUARTER_ORDINALS[qi] || `Q${qi + 1}`);
      let qText;
      if (qLabels.length === 1) {
        qText = `${qLabels[0]} quarter`;
      } else if (qLabels.length === 2) {
        qText = `${qLabels[0]} and ${qLabels[1]} quarter`;
      } else {
        qText = `${qLabels.slice(0, -1).join(', ')}, and ${qLabels[qLabels.length - 1]} quarter`;
      }

      const jurLabel = toTitleCase(group.jurName);
      let statusText;
      if (group.status === 'paid') statusText = `paid to ${jurLabel}`;
      else if (group.status === 'due') statusText = `due to ${jurLabel}`;
      else if (group.status === 'delinquent') statusText = `delinquent to ${jurLabel}`;
      else if (group.status.includes('partially')) statusText = `partially delinquent to ${jurLabel}`;
      else statusText = `${group.status} to ${jurLabel}`;

      phraseParts.push(`${qText} taxes are ${statusText}`);
    }

    if (phraseParts.length > 0) notes.push(`${yr} ${phraseParts.join(', and ')}.`);
  }

  notes.push('Taxes are paid quarterly;');

  const allDueDates = [];
  const allDlqDates = [];
  for (const yr of sortedYears) {
    const sorted = [...byYear[yr]].sort((a, b) => getQuarterIndex(a) - getQuarterIndex(b));
    for (const inst of sorted) {
      const due = fmtDate(inst.due_date);
      const dlq = fmtDate(inst.dlq_date);
      if (due && !allDueDates.includes(due)) allDueDates.push(due);
      if (dlq && !allDlqDates.includes(dlq)) allDlqDates.push(dlq);
    }
  }

  if (allDueDates.length > 0) {
    const dueStr =
      allDueDates.length <= 2
        ? allDueDates.join(' and ')
        : allDueDates.slice(0, -1).join(', ') + ', and ' + allDueDates[allDueDates.length - 1];
    let line = `normal due dates are ${dueStr}`;
    if (allDlqDates.length > 0) {
      const dlqStr =
        allDlqDates.length <= 2
          ? allDlqDates.join(' and ')
          : allDlqDates.slice(0, -1).join(', ') + ', and ' + allDlqDates[allDlqDates.length - 1];
      line += ` and become delinquent after ${dlqStr}`;
    }
    notes.push(line + '.');
  }

  return notes.join(' ');
};
